import { aiAnalysisHttpApi } from './http'
import type { AiAnalysisStatus } from './types'

type Listener = (status: AiAnalysisStatus | null) => void

export function analysisOutcome(status: AiAnalysisStatus) {
  const terminal = (value: string | null) =>
    value === 'completed' || value === 'failed' || value === 'cancelled'
  if (!terminal(status.criteriaStatus) || !terminal(status.descriptionStatus)) return null
  if (status.criteriaStatus === 'cancelled' || status.descriptionStatus === 'cancelled')
    return 'cancelled' as const
  if (status.criteriaStatus === 'failed' || status.descriptionStatus === 'failed')
    return 'failed' as const
  if (status.criteriaStatus === 'completed' && status.error === 'no_criteria_documents')
    return 'noDocuments' as const
  return 'success' as const
}

/** One request per batch every five seconds. Only server outcomes end a run. */
export function createAnalysisPoller() {
  const listeners = new Map<number, Listener>()
  let timer: ReturnType<typeof setTimeout> | undefined
  let controller: AbortController | undefined
  let disposed = false

  function schedule() {
    if (!disposed && listeners.size > 0 && timer === undefined && controller === undefined)
      timer = setTimeout(() => void poll(), 5000)
  }

  async function poll() {
    timer = undefined
    const request = new AbortController()
    controller = request
    const snapshot = [...listeners.entries()]
    try {
      for (let offset = 0; offset < snapshot.length; offset += 100) {
        const chunk = snapshot.slice(offset, offset + 100)
        const statuses = await aiAnalysisHttpApi.statuses(
          chunk.map(([id]) => id),
          { signal: request.signal },
        )
        if (disposed || request.signal.aborted) return
        const byId = new Map(statuses.map((status) => [status.procurementId, status]))
        for (const [id, listener] of chunk) {
          // Ignore responses for a stopped or superseded subscription.
          if (listeners.get(id) === listener) listener(byId.get(id) ?? null)
        }
      }
    } catch {
      // A failed status request says nothing about whether the calculation failed.
      // Keep tracking the run, with the same bounded polling frequency.
    } finally {
      if (controller === request) controller = undefined
      schedule()
    }
  }

  return {
    track(id: number, listener: Listener) {
      listeners.set(id, listener)
      schedule()
    },
    stop(id: number) {
      listeners.delete(id)
      if (listeners.size === 0) {
        clearTimeout(timer)
        timer = undefined
        controller?.abort()
      }
    },
    dispose() {
      disposed = true
      listeners.clear()
      clearTimeout(timer)
      controller?.abort()
    },
  }
}
